import { Modal } from '@/shared/components/Modal'
import { ShellSnippet } from '@/shared/components/ShellSnippet'
import { SourceRepoLink } from '@/shared/components/SourceRepoLink'
import { useGuideNavigate } from '@/features/integrations/components/EmptyGuide'
import { DatabaseIcon, ExternalLinkIcon } from '@/shared/components/Icons'
import type { GraphNode } from '@/lib/apiTypes'
import s from '@/features/graph/components/GraphLockFileHelper.module.css'

/**
 * What a state node with no lock file opens.
 *
 * Most workspaces are in this state, so on the canvas they are disconnected nodes with no
 * provider pins to draw. That is not noise to hide — it is an unfinished setup step, and the
 * graph is where you notice it. This turns the orphan into something actionable: it names the
 * workspace, shows which pipeline and commit last pushed it, and hands over the command and
 * the guide to close the gap.
 */
export function GraphLockFileHelper({
  node,
  onClose,
  onOpenWorkspace,
}: {
  node: GraphNode
  onClose: () => void
  /** The page owns navigation, so this component needs no org slug of its own. */
  onOpenWorkspace: (workspaceName: string) => void
}) {
  const goToGuide = useGuideNavigate()
  const workspace = node.workspace_name ?? node.label

  return (
    <Modal
      track="graph_lock_file_helper"
      kicker="No lock file"
      title={workspace}
      onClose={onClose}
      maxWidth={620}
    >
      <p className={s.lead}>
        Terramantle can&apos;t see which provider versions <strong>{workspace}</strong> pins, because
        no <code className={s.code}>.terraform.lock.hcl</code> has been uploaded for it. Until one
        is, this workspace is excluded from provider CVE and outdated-version warnings — and it has
        no provider edges to draw on the graph.
      </p>

      <div className={s.factGrid}>
        <div className={s.factLabel}>Workspace</div>
        <div className={s.factValue}>
          <span className={s.factIcon}><DatabaseIcon size={13} /></span>
          <button className={s.linkBtn} onClick={() => onOpenWorkspace(workspace)}>
            {workspace} <ExternalLinkIcon size={11} />
          </button>
        </div>

        {node.serial != null && (
          <>
            <div className={s.factLabel}>Latest state</div>
            <div className={s.factValue}>serial {node.serial}</div>
          </>
        )}

        {/* Provenance exists only for pushes authenticated by VCS OIDC. It is null for human and
            bot-token pushes and for promotes, which is the common case — so this block is absent
            rather than rendered empty. */}
        {node.vcs_repository && (
          <>
            <div className={s.factLabel}>Pushed from</div>
            <div className={s.factValue}>
              <SourceRepoLink
                provider={node.vcs_provider ?? null}
                repository={node.vcs_repository}
                gitRef={node.vcs_ref ?? null}
                refType={node.vcs_ref_type ?? null}
                sha={node.vcs_sha ?? null}
                commitUrl={node.vcs_commit_url ?? null}
                verb="pushed"
              />
              <span className={s.repoText}>
                {node.vcs_repository}
                {node.vcs_ref && <span className={s.refText}> · {node.vcs_ref}</span>}
                {node.vcs_sha && <span className={s.shaText}> · {node.vcs_sha.slice(0, 8)}</span>}
              </span>
            </div>
          </>
        )}
      </div>

      <div className={s.sectionLabel}>// upload it</div>
      <p className={s.body}>
        Run this where the workspace&apos;s <code className={s.code}>.terraform.lock.hcl</code> lives —
        usually the same CI job that runs <code className={s.code}>init</code>:
      </p>
      <ShellSnippet code={`terramantle publish --workspace ${workspace}`} language="bash" />
      <p className={s.bodyMuted}>
        Add <code className={s.code}>--all</code> to upload every workspace&apos;s lock file in one
        go. Re-running it replaces the previous upload, so it is safe on every build.
      </p>

      <div className={s.footerLinks}>
        <button className="btn btn-sm" onClick={() => goToGuide('lock-files')}>
          Provider Lock Files guide →
        </button>
        <button className="btn btn-sm" onClick={() => goToGuide('storing-state')}>
          Storing State guide →
        </button>
      </div>
    </Modal>
  )
}
